/**
 * Sign-in through the Entra identity broker (entra-identity-broker-cdk), adapted from its admin
 * console. This site is an ordinary broker app client: Cognito managed login on the broker's domain,
 * PKCE S256, public client, identity_provider pinned so users go straight to Microsoft.
 */

function buildCoreAuthorizeUrl(params: {
  domainName: string;
  idpName: string;
  clientId: string;
  redirectUri: string;
  scopes: readonly string[];
  extraParams: Record<string, string>;
}): string {
  const url = new URL(`https://${params.domainName}/oauth2/authorize`);
  url.searchParams.set('identity_provider', params.idpName);
  url.searchParams.set('response_type', 'code');
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('redirect_uri', params.redirectUri);
  url.searchParams.set('scope', params.scopes.join(' '));
  for (const [key, value] of Object.entries(params.extraParams)) url.searchParams.set(key, value);
  return url.toString();
}

function buildCoreLogoutUrl(params: { domainName: string; clientId: string; logoutUri: string }): string {
  const url = new URL(`https://${params.domainName}/logout`);
  url.searchParams.set('client_id', params.clientId);
  url.searchParams.set('logout_uri', params.logoutUri);
  return url.toString();
}

/** Bootstrap values served by GET /api/config. */
export interface RuntimeConfig {
  userPoolId: string;
  clientId: string;
  /** Byte-exact as registered with the broker - never rebuild this from window.location. */
  redirectUri: string;
  /**
   * Byte-exact as registered with the broker (`app:add --logout-uri`). Its absence makes sign-out
   * fall back to local: sending a logout_uri Cognito does not know would fail with
   * redirect_mismatch and strand the user on an error page.
   */
  logoutUri?: string;
  domainName: string;
  idpName: string;
  scopes: string[];
  entraTenantId: string;
  region: string;
}

export interface TokenSet {
  idToken: string;
  accessToken: string;
  refreshToken?: string;
  /** Epoch milliseconds. */
  expiresAt: number;
}

interface TokenResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
  expires_in: number;
}

/**
 * The broker's OAuth endpoints.
 *
 * revokeEndpoint and issuerUrl are exported because the broker's implementation prompt hands them to an
 * app's developers verbatim, and a second copy of the paths written out there is exactly the drift
 * login-url.ts exists to prevent. The prompt has to describe the flow this file actually performs.
 */
export function tokenEndpoint(config: RuntimeConfig): string {
  return `https://${config.domainName}/oauth2/token`;
}

export function revokeEndpoint(config: RuntimeConfig): string {
  return `https://${config.domainName}/oauth2/revoke`;
}

/**
 * This site's /logout URL, or undefined when no logout URI is registered for it.
 *
 * Undefined is not a failure - it is the state every deployment is in until an operator registers
 * one, and the caller signs out locally instead.
 */
export function buildLogoutUrl(config: RuntimeConfig): string | undefined {
  if (!config.logoutUri) return undefined;
  return buildCoreLogoutUrl({
    domainName: config.domainName,
    clientId: config.clientId,
    logoutUri: config.logoutUri,
  });
}

/**
 * The Cognito issuer, which is where OIDC discovery and the JWKS actually live.
 *
 * Never the custom domain: id.apps.everyhome.org returns 404 on /.well-known/openid-configuration
 * by design, so a client pointed at it fails discovery with a confusing error. The authorize and
 * token endpoints are on the custom domain; validation material is here.
 */
export function issuerUrl(config: RuntimeConfig): string {
  return `https://cognito-idp.${config.region}.amazonaws.com/${config.userPoolId}`;
}

/** This site's sign-in redirect: the broker's authorize URL plus the PKCE parameters. */
export function buildAuthorizeUrl(
  config: RuntimeConfig,
  params: { state: string; challenge: string },
): string {
  return buildCoreAuthorizeUrl({
    domainName: config.domainName,
    idpName: config.idpName,
    clientId: config.clientId,
    redirectUri: config.redirectUri,
    scopes: config.scopes,
    extraParams: {
      code_challenge: params.challenge,
      code_challenge_method: 'S256',
      state: params.state,
    },
  });
}

async function postToken(config: RuntimeConfig, body: URLSearchParams): Promise<TokenSet> {
  const response = await fetch(tokenEndpoint(config), {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => '');
    throw new Error(`Token endpoint returned ${response.status}: ${text}`);
  }

  const json = (await response.json()) as TokenResponse;
  return {
    idToken: json.id_token,
    accessToken: json.access_token,
    refreshToken: json.refresh_token,
    expiresAt: Date.now() + json.expires_in * 1000,
  };
}

export function exchangeCode(
  config: RuntimeConfig,
  params: { code: string; verifier: string },
): Promise<TokenSet> {
  return postToken(
    config,
    new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: config.clientId,
      code: params.code,
      // Must match the authorize request byte for byte, including any trailing slash.
      redirect_uri: config.redirectUri,
      code_verifier: params.verifier,
    }),
  );
}

/**
 * Cognito does not return a new refresh token on this call, so callers keep the original.
 *
 * A refresh re-runs the pre-token-generation trigger, which is a feature rather than an
 * implementation detail: if a user is removed from the access group, or this site's app client is
 * disabled, the next refresh fails and the session ends within a couple of minutes.
 */
export async function refreshTokens(
  config: RuntimeConfig,
  refreshToken: string,
): Promise<TokenSet> {
  const tokens = await postToken(
    config,
    new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: config.clientId,
      refresh_token: refreshToken,
    }),
  );
  return { ...tokens, refreshToken };
}

/** Best-effort. EnableTokenRevocation is set on every broker app client. */
export async function revokeToken(config: RuntimeConfig, refreshToken: string): Promise<void> {
  await fetch(revokeEndpoint(config), {
    method: 'POST',
    headers: { 'content-type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ token: refreshToken, client_id: config.clientId }),
  }).catch(() => {
    // Sign-out must not fail because revocation did. The in-memory tokens are dropped regardless.
  });
}

